import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from '../../utils/permissionUtils';
import DataTable from "react-data-table-component";
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";

const ExpandedComponent = ({ data }) => {
  const theme = useTheme();
  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Questions:</strong> {data.questions}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Answers:</strong> {data.answers}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>User Issue:</strong> {data.user_issue}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>User Feedback:</strong> {data.user_feedback}
      </Typography>
      <Typography variant="body2">
        <strong>Create Date:</strong> {data.created_date}
      </Typography>
    </Box>
  );
};

export default function ListUserFeedback() {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { chatbotId: paramsChatbotId } = useParams();

  // Permission states
  const [canView, setCanView] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('feedbacks', 'view');
        setCanView(viewPerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  useEffect(() => {
    document.title = 'User Feedback Lists | KlonIT Web Application';
    if (canView && !checkingPermissions) {
      fetchDocuments();
    }
  }, [canView, checkingPermissions]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/get-user-feedback`, { params });
      const responseData = typeof response.data.data === 'string' 
        ? JSON.parse(response.data.data) 
        : response.data.data;

      if (responseData && responseData.feedbacks) {
        setDocuments(responseData.feedbacks);
        setFilteredDocuments(responseData.feedbacks);
      } else {
        setDocuments([]);
        setFilteredDocuments([]);
      }
      setError(null);
    } catch (error) {
      console.error("Error fetching user feedback:", error);
      if (error.response?.status === 401) {
        setError("Authentication error: " + error.response.data.message);
      } else {
        setError("Error fetching user feedback: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = documents.filter(doc =>
      doc.questions.toLowerCase().includes(value) ||
      doc.answers.toLowerCase().includes(value) ||
      doc.user_issue.toLowerCase().includes(value) ||
      doc.user_feedback.toLowerCase().includes(value) ||
      doc.created_date.toLowerCase().includes(value)
    );
    setFilteredDocuments(filtered);
  };

  const columns = [
    {
      name: "Questions",
      selector: row => row.questions,
      sortable: true,
      maxWidth: '315px',
    },
    {
      name: "Answers",
      selector: row => row.answers,
      sortable: true,
      maxWidth: '315px',
    },
    {
      name: "User Issue",
      selector: row => row.user_issue,
      sortable: true,
      maxWidth: '315px',
    },
    {
      name: "User Feedback",
      selector: row => row.user_feedback,
      sortable: true,
      maxWidth: '315px',
    },
    {
      name: "Create Date",
      selector: row => row.created_date,
      sortable: true,
      maxWidth: '315px',
    },
  ];

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view User Feedbacks settings. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
        User <strong>Feedbacks</strong>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="text.primary">
          View and manage user feedback submissions
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          sx={{ minWidth: '250px' }}
        />
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading feedbacks...</Typography>
        </Box>
      ) : filteredDocuments.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i className="fa fa-comments-o fa-3x text-muted" />
          <Typography mt={2} color="text.primary">
            No feedback available
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={filteredDocuments}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          customStyles={customDataTableStyles}
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}
    </Paper>
  );
}